import React, { useRef, useState, useEffect, useCallback } from 'react';

const VAST_TAG_URL = 'https://s.magsrv.com/v1/vast.php?idz=5856446';
const IMA_SDK_SRC  = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
const SKIP_AFTER   = 8; // seconds before skip button appears

/* ── IMA loader (singleton) ──────────────────────────────────── */
let imaPromise = null;
function loadIMA() {
    if (window.google?.ima) return Promise.resolve();
    if (!imaPromise) {
        imaPromise = new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = IMA_SDK_SRC;
            s.onload = resolve;
            s.onerror = () => { imaPromise = null; reject(); };
            document.head.appendChild(s);
        });
    }
    return imaPromise;
}

/* ── Time formatter ──────────────────────────────────────────── */
function fmtTime(secs) {
    if (!secs || isNaN(secs) || !isFinite(secs)) return '0:00';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    return `${m}:${String(s).padStart(2,'0')}`;
}

/* ── SVG icons ───────────────────────────────────────────────── */
const IconPlay = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
        <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
);
const IconPause = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
        <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
    </svg>
);
const IconVolume = ({ muted, level }) => muted || level === 0 ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
        <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
    </svg>
) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
        {level > 0.5
            ? <><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></>
            : <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>}
    </svg>
);
const IconFS = ({ on }) => on ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
        <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
    </svg>
) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
    </svg>
);
const IconSkip = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/>
    </svg>
);

/* ── Main component ──────────────────────────────────────────── */
export default function VideoPlayer({ src, poster, title, videoRef: extRef }) {
    const intRef       = useRef(null);
    const videoRef     = extRef || intRef;
    const containerRef = useRef(null);
    const adContRef    = useRef(null);
    const barRef       = useRef(null);
    const hideTimer    = useRef(null);
    const adTimer      = useRef(null);
    const mgr          = useRef(null);
    const loader       = useRef(null);
    const initiated    = useRef(false);

    const [playing,      setPlaying]     = useState(false);
    const [curTime,      setCurTime]     = useState(0);
    const [duration,     setDuration]    = useState(0);
    const [buffered,     setBuffered]    = useState(0);
    const [vol,          setVol]         = useState(1);
    const [muted,        setMuted]       = useState(false);
    const [fs,           setFs]          = useState(false);
    const [ctrlVisible,  setCtrlVisible] = useState(true);
    const [adActive,     setAdActive]    = useState(false);
    const [adCountdown,  setAdCountdown] = useState(SKIP_AFTER);
    const [adSkippable,  setAdSkippable] = useState(false);
    const [started,      setStarted]     = useState(false);
    const [dragging,     setDragging]    = useState(false);

    /* controls auto-hide */
    const bumpCtrl = useCallback(() => {
        setCtrlVisible(true);
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => {
            if (!dragging) setCtrlVisible(false);
        }, 3000);
    }, [dragging]);

    /* fullscreen change */
    useEffect(() => {
        const h = () => setFs(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', h);
        return () => document.removeEventListener('fullscreenchange', h);
    }, []);

    /* pre-warm IMA + cleanup */
    useEffect(() => {
        loadIMA().catch(() => {});
        return () => {
            clearTimeout(hideTimer.current);
            clearInterval(adTimer.current);
            mgr.current?.destroy();
        };
    }, []);

    /* ── VAST / IMA init ───────────────────────────────────────── */
    const startAds = useCallback(async () => {
        if (initiated.current) return;
        initiated.current = true;

        const video = videoRef.current;
        const adCont = adContRef.current;
        if (!video || !adCont) { video?.play(); return; }

        try { await loadIMA(); }
        catch { video.play(); return; }

        const ima = window.google.ima;
        const adc = new ima.AdDisplayContainer(adCont, video);
        adc.initialize();

        const adsLoader = new ima.AdsLoader(adc);
        loader.current = adsLoader;

        adsLoader.addEventListener(
            ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
            (ev) => {
                const adsManager = ev.getAdsManager(video);
                mgr.current = adsManager;

                adsManager.addEventListener(ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
                    video.pause();
                    setAdActive(true);
                    setAdCountdown(SKIP_AFTER);
                    setAdSkippable(false);
                    let cnt = SKIP_AFTER;
                    adTimer.current = setInterval(() => {
                        cnt--;
                        setAdCountdown(cnt);
                        if (cnt <= 0) {
                            clearInterval(adTimer.current);
                            setAdSkippable(true);
                        }
                    }, 1000);
                });

                adsManager.addEventListener(ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
                    clearInterval(adTimer.current);
                    setAdActive(false);
                    setAdSkippable(false);
                    video.play();
                });

                adsManager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
                    clearInterval(adTimer.current);
                    setAdActive(false);
                    setAdSkippable(false);
                });

                adsManager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, () => {
                    clearInterval(adTimer.current);
                    setAdActive(false);
                    video.play();
                });

                try {
                    adsManager.init(
                        video.offsetWidth  || 640,
                        video.offsetHeight || 360,
                        ima.ViewMode.NORMAL
                    );
                    adsManager.start();
                } catch { video.play(); }
            }
        );

        adsLoader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, () => {
            setAdActive(false);
            video.play();
        });

        video.addEventListener('ended', () => loader.current?.contentComplete(), { once: true });

        const req = new ima.AdsRequest();
        req.adTagUrl             = VAST_TAG_URL;
        req.linearAdSlotWidth    = video.offsetWidth  || 640;
        req.linearAdSlotHeight   = video.offsetHeight || 360;
        req.nonLinearAdSlotWidth = video.offsetWidth  || 640;
        req.nonLinearAdSlotHeight = 100;
        adsLoader.requestAds(req);
    }, [videoRef]);

    /* ── Controls ────────────────────────────────────────────── */
    const togglePlay = () => {
        const v = videoRef.current;
        if (!v || adActive) return;
        if (!started) {
            setStarted(true);
            startAds();
            return;
        }
        v.paused ? v.play() : v.pause();
    };

    const skipAd = () => {
        if (!adSkippable) return;
        clearInterval(adTimer.current);
        try { mgr.current?.stop(); } catch {}
        setAdActive(false);
        setAdSkippable(false);
        videoRef.current?.play();
    };

    const seekTo = useCallback((clientX) => {
        const bar = barRef.current;
        const v   = videoRef.current;
        if (!bar || !v || !duration) return;
        const r = bar.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
        v.currentTime = ratio * duration;
    }, [duration, videoRef]);

    const onBarClick   = (e) => seekTo(e.clientX);
    const onBarMouseDown = (e) => {
        setDragging(true);
        seekTo(e.clientX);
        const onMove = (ev) => seekTo(ev.clientX);
        const onUp   = () => { setDragging(false); window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup',   onUp);
    };

    const onVolChange = (e) => {
        const v = videoRef.current;
        if (!v) return;
        const n = parseFloat(e.target.value);
        v.volume = n; v.muted = n === 0;
        setVol(n); setMuted(n === 0);
    };

    const toggleMute = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.muted) { v.muted = false; if (v.volume === 0) { v.volume = 0.5; setVol(0.5); } }
        else { v.muted = true; }
        setMuted(v.muted);
    };

    const toggleFS = () => {
        const el = containerRef.current;
        const v  = videoRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            (el.requestFullscreen || el.webkitRequestFullscreen || (() => v?.webkitEnterFullscreen?.())).call(el);
        } else {
            document.exitFullscreen?.();
        }
    };

    /* progress */
    const pct  = duration ? (curTime  / duration) * 100 : 0;
    const bpct = duration ? (buffered / duration) * 100 : 0;
    const showBigPlay = !started || (!playing && !adActive);

    return (
        <div
            ref={containerRef}
            onMouseMove={bumpCtrl}
            onMouseEnter={bumpCtrl}
            onMouseLeave={() => { if (playing && !adActive) setCtrlVisible(false); }}
            onTouchStart={bumpCtrl}
            style={{
                position: 'relative', width: '100%', aspectRatio: '16/9',
                background: '#000', overflow: 'hidden',
                cursor: ctrlVisible ? 'default' : 'none',
                userSelect: 'none', WebkitUserSelect: 'none',
            }}
        >
            {/* Video */}
            <video
                ref={videoRef}
                key={src}
                src={src}
                poster={poster}
                playsInline
                preload="metadata"
                onPlay={() => { setPlaying(true); bumpCtrl(); }}
                onPause={() => { setPlaying(false); setCtrlVisible(true); }}
                onEnded={() => { setPlaying(false); setCtrlVisible(true); loader.current?.contentComplete(); }}
                onTimeUpdate={() => {
                    const v = videoRef.current;
                    if (!v) return;
                    setCurTime(v.currentTime);
                    if (v.buffered.length) setBuffered(v.buffered.end(v.buffered.length - 1));
                }}
                onDurationChange={() => setDuration(videoRef.current?.duration || 0)}
                style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
                aria-label={title}
            />

            {/* IMA ad container */}
            <div
                ref={adContRef}
                style={{ position: 'absolute', inset: 0, pointerEvents: adActive ? 'auto' : 'none' }}
            />

            {/* ── Ad overlay UI ──────────────────────────────── */}
            {adActive && (
                <div style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none',
                    display: 'flex', flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '10px 14px 14px',
                }}>
                    {/* "РЕКЛАМА" badge top-left */}
                    <div style={{
                        alignSelf: 'flex-start',
                        background: 'rgba(0,0,0,0.55)',
                        border: '1px solid rgba(255,255,255,0.14)',
                        borderRadius: 5, padding: '3px 8px',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 9.5, letterSpacing: 1, color: '#A1A1AA',
                    }}>РЕКЛАМА</div>

                    {/* Skip button bottom-right */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', pointerEvents: 'auto' }}>
                        {adSkippable ? (
                            <button
                                onClick={skipAd}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 7,
                                    padding: '9px 16px',
                                    background: 'rgba(10,10,11,0.88)',
                                    border: '1px solid rgba(255,255,255,0.28)',
                                    borderRadius: 7, cursor: 'pointer',
                                    color: '#fff', fontSize: 13, fontWeight: 600,
                                    fontFamily: "'Manrope', sans-serif",
                                    backdropFilter: 'blur(10px)',
                                    transition: 'border-color 0.15s',
                                }}
                            >
                                Пропустить <IconSkip />
                            </button>
                        ) : (
                            <div style={{
                                padding: '9px 16px',
                                background: 'rgba(10,10,11,0.72)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 7,
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: 11.5, color: '#A1A1AA',
                            }}>
                                Пропустить через {adCountdown} с
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Center big play button ──────────────────────── */}
            {showBigPlay && !adActive && (
                <div
                    onClick={togglePlay}
                    style={{
                        position: 'absolute', inset: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <div style={{
                        width: 68, height: 68, borderRadius: '50%',
                        background: 'rgba(230,0,115,0.92)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 0 48px rgba(230,0,115,0.45)',
                        transition: 'transform 0.15s',
                    }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff">
                            <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                    </div>
                </div>
            )}

            {/* ── Click area to toggle play (while playing) ────── */}
            {started && playing && !adActive && (
                <div
                    onClick={togglePlay}
                    style={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
                />
            )}

            {/* ── Controls bar ───────────────────────────────────── */}
            {!adActive && (
                <div
                    style={{
                        position: 'absolute', left: 0, right: 0, bottom: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.88) 100%)',
                        padding: '40px 14px 10px',
                        opacity: ctrlVisible ? 1 : 0,
                        transition: 'opacity 0.3s',
                        pointerEvents: ctrlVisible ? 'auto' : 'none',
                    }}
                    onMouseMove={(e) => e.stopPropagation()}
                >
                    {/* Progress bar */}
                    <div
                        ref={barRef}
                        onClick={onBarClick}
                        onMouseDown={onBarMouseDown}
                        style={{
                            height: 4, borderRadius: 4,
                            background: 'rgba(255,255,255,0.18)',
                            cursor: 'pointer', marginBottom: 10,
                            position: 'relative',
                        }}
                    >
                        <div style={{
                            position: 'absolute', left: 0, top: 0, height: '100%',
                            width: `${bpct}%`,
                            background: 'rgba(255,255,255,0.28)', borderRadius: 4,
                            transition: 'width 0.3s',
                        }} />
                        <div style={{
                            position: 'absolute', left: 0, top: 0, height: '100%',
                            width: `${pct}%`,
                            background: '#e60073', borderRadius: 4,
                        }} />
                        <div style={{
                            position: 'absolute', top: '50%',
                            left: `${pct}%`,
                            transform: 'translate(-50%, -50%)',
                            width: 13, height: 13, borderRadius: '50%',
                            background: '#e60073',
                            boxShadow: '0 0 8px rgba(230,0,115,0.7)',
                            transition: dragging ? 'none' : 'left 0.1s',
                        }} />
                    </div>

                    {/* Bottom row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                        {/* Play/Pause */}
                        <button onClick={togglePlay} style={btnStyle}>
                            {playing ? <IconPause /> : <IconPlay />}
                        </button>

                        {/* Volume */}
                        <button onClick={toggleMute} style={btnStyle}>
                            <IconVolume muted={muted} level={vol} />
                        </button>
                        <input
                            type="range" min="0" max="1" step="0.05"
                            value={muted ? 0 : vol}
                            onChange={onVolChange}
                            style={{
                                width: 58, cursor: 'pointer',
                                accentColor: '#e60073', flexShrink: 0,
                            }}
                        />

                        {/* Time */}
                        <div style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 11, color: 'rgba(255,255,255,0.75)',
                            whiteSpace: 'nowrap', flex: 1,
                        }}>
                            {fmtTime(curTime)}
                            <span style={{ color: 'rgba(255,255,255,0.4)', margin: '0 4px' }}>/</span>
                            {fmtTime(duration)}
                        </div>

                        {/* Fullscreen */}
                        <button onClick={toggleFS} style={btnStyle}>
                            <IconFS on={fs} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const btnStyle = {
    background: 'transparent', border: 0, cursor: 'pointer',
    padding: 5, display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 6, flexShrink: 0,
};
